import React, {useEffect, useState, useRef} from 'react';
import {useSelectedLocale} from './selected-locale';
import {useChangeLocale} from './change-locale';
import {useBootstrapData} from '../core/bootstrap-data/bootstrap-data-context';
import {getBootstrapData} from '../core/bootstrap-data/use-backend-bootstrap-data';
import {Button} from '../ui/buttons/button';
import {useValueLists} from '../http/value-lists';

/**
 * Тестовая страница для отладки локализации
 * 
 * Создано: 2025-01-XX
 * 
 * Функциональность:
 * - Показывает состояние локализации в реальном времени
 * - Логирует все изменения локализации
 * - Показывает bootstrap data и window.bootstrapData
 * - Позволяет менять язык и видеть все логи
 */
export function TestLocalePage() {
  const {locale, localeCode, lines} = useSelectedLocale();
  const changeLocale = useChangeLocale();
  const {data: bootstrapData, mergeBootstrapData} = useBootstrapData();
  const {data} = useValueLists(['localizations']);
  const [logs, setLogs] = useState<Array<{time: string; type: string; message: string; data?: any}>>([]);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Добавить лог
  const addLog = (type: string, message: string, data?: any) => {
    const time = new Date().toLocaleTimeString('ru-RU', {hour12: false});
    setLogs(prev => [...prev, {time, type, message, data}]);
  };

  // Прокрутка к последнему логу
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({behavior: 'smooth'});
  }, [logs]);

  // Отслеживание изменений locale
  useEffect(() => {
    addLog('INFO', 'Locale changed', {
      localeCode,
      localeName: locale?.name,
      hasLines: !!lines,
      linesCount: lines ? Object.keys(lines).length : 0,
    });
  }, [localeCode, locale?.name, lines]);

  // Отслеживание изменений bootstrapData.i18n
  useEffect(() => {
    const currentI18n = bootstrapData?.i18n;
    addLog('INFO', 'Bootstrap data i18n changed', {
      language: currentI18n?.language,
      name: currentI18n?.name,
      hasLines: !!currentI18n?.lines,
      linesCount: currentI18n?.lines ? Object.keys(currentI18n.lines).length : 0,
    });
  }, [bootstrapData?.i18n]);

  // Отслеживание window.bootstrapData
  useEffect(() => {
    const checkWindowBootstrapData = () => {
      if (typeof window !== 'undefined' && (window as any).bootstrapData) {
        try {
          const windowData = typeof (window as any).bootstrapData === 'string'
            ? JSON.parse((window as any).bootstrapData)
            : (window as any).bootstrapData;
          const windowI18n = windowData?.i18n;
          addLog('INFO', 'window.bootstrapData checked', {
            language: windowI18n?.language,
            name: windowI18n?.name,
            hasLines: !!windowI18n?.lines,
            linesCount: windowI18n?.lines ? Object.keys(windowI18n.lines).length : 0,
          });
        } catch (e) {
          addLog('ERROR', 'Failed to parse window.bootstrapData', {error: String(e)});
        }
      } else {
        addLog('WARNING', 'window.bootstrapData is not available');
      }
    };

    checkWindowBootstrapData();
    const interval = setInterval(checkWindowBootstrapData, 2000);
    return () => clearInterval(interval);
  }, []);

  // Обработка успешной смены языка
  useEffect(() => {
    if (changeLocale.isSuccess) {
      addLog('SUCCESS', 'Locale change mutation succeeded', {
        data: changeLocale.data,
      });
    }
  }, [changeLocale.isSuccess, changeLocale.data]);

  // Обработка ошибки смены языка
  useEffect(() => {
    if (changeLocale.isError) {
      addLog('ERROR', 'Locale change mutation failed', {
        error: changeLocale.error,
      });
    }
  }, [changeLocale.isError, changeLocale.error]);

  const handleChangeLocale = (newLocale: string) => {
    addLog('ACTION', `Changing locale to: ${newLocale}`);
    changeLocale.mutate({locale: newLocale});
  };

  const handleRefresh = () => {
    addLog('ACTION', 'Manual refresh triggered');
    const currentData = getBootstrapData();
    addLog('INFO', 'Current bootstrap data from queryClient', {
      language: currentData?.i18n?.language,
      hasLines: !!currentData?.i18n?.lines,
    });
  };

  const handleCheckWindow = () => {
    if (typeof window !== 'undefined' && (window as any).bootstrapData) {
      try {
        const windowData = typeof (window as any).bootstrapData === 'string'
          ? JSON.parse((window as any).bootstrapData)
          : (window as any).bootstrapData;
        addLog('INFO', 'window.bootstrapData snapshot', windowData);
      } catch (e) {
        addLog('ERROR', 'Failed to parse window.bootstrapData', {error: String(e)});
      }
    } else {
      addLog('WARNING', 'window.bootstrapData is not available');
    }
  };

  const clearLogs = () => {
    setLogs([]);
    addLog('INFO', 'Logs cleared');
  };

  return (
    <div style={{padding: '20px', maxWidth: '1400px', margin: '0 auto'}}>
      <h1 style={{marginBottom: '20px'}}>🔍 Тестовая страница: Отладка локализации</h1>

      {/* Текущее состояние */}
      <div style={{
        background: '#f5f5f5',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
      }}>
        <h2 style={{marginTop: 0}}>Текущее состояние</h2>
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
          <div>
            <h3>useSelectedLocale()</h3>
            <pre style={{background: '#fff', padding: '10px', borderRadius: '4px', overflow: 'auto'}}>
              {JSON.stringify({
                localeCode,
                localeName: locale?.name,
                hasLines: !!lines,
                linesCount: lines ? Object.keys(lines).length : 0,
                sampleLines: lines ? Object.keys(lines).slice(0, 5) : [],
              }, null, 2)}
            </pre>
          </div>
          <div>
            <h3>bootstrapData.i18n</h3>
            <pre style={{background: '#fff', padding: '10px', borderRadius: '4px', overflow: 'auto'}}>
              {JSON.stringify({
                language: bootstrapData?.i18n?.language,
                name: bootstrapData?.i18n?.name,
                hasLines: !!bootstrapData?.i18n?.lines,
                linesCount: bootstrapData?.i18n?.lines ? Object.keys(bootstrapData.i18n.lines).length : 0,
                sampleLines: bootstrapData?.i18n?.lines ? Object.keys(bootstrapData.i18n.lines).slice(0, 5) : [],
              }, null, 2)}
            </pre>
          </div>
        </div>
      </div>

      {/* Управление */}
      <div style={{
        background: '#f5f5f5',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
      }}>
        <h2 style={{marginTop: 0}}>Управление</h2>
        <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px'}}>
          {data?.localizations?.map((loc: any) => (
            <Button
              key={loc.language}
              variant={localeCode === loc.language ? 'flat' : 'outline'}
              color={localeCode === loc.language ? 'primary' : undefined}
              disabled={changeLocale.isPending}
              onClick={() => handleChangeLocale(loc.language)}
            >
              {loc.name} ({loc.language})
            </Button>
          ))}
        </div>
        <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
          <Button onClick={handleRefresh}>Обновить данные</Button>
          <Button onClick={handleCheckWindow}>Проверить window.bootstrapData</Button>
          <Button onClick={clearLogs} variant="outline">Очистить логи</Button>
        </div>
        {changeLocale.isPending && (
          <div style={{marginTop: '10px', color: '#2196F3'}}>⏳ Смена языка...</div>
        )}
      </div>

      {/* Логи */}
      <div style={{
        background: '#f5f5f5',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
      }}>
        <h2 style={{marginTop: 0}}>Логи ({logs.length})</h2>
        <div style={{
          background: '#fff',
          padding: '10px',
          borderRadius: '4px',
          maxHeight: '500px',
          overflowY: 'auto',
          fontFamily: 'monospace',
          fontSize: '12px',
        }}>
          {logs.map((log, index) => (
            <div
              key={index}
              style={{
                padding: '5px',
                borderBottom: '1px solid #eee',
                color: log.type === 'ERROR' ? '#f44336' :
                       log.type === 'WARNING' ? '#ff9800' :
                       log.type === 'SUCCESS' ? '#4CAF50' :
                       log.type === 'ACTION' ? '#2196F3' : '#333',
              }}
            >
              <span style={{color: '#999'}}>[{log.time}]</span>{' '}
              <strong>[{log.type}]</strong> {log.message}
              {log.data && (
                <pre style={{margin: '5px 0 0 20px', fontSize: '11px', color: '#666'}}>
                  {JSON.stringify(log.data, null, 2)}
                </pre>
              )}
            </div>
          ))}
          <div ref={logsEndRef} />
        </div>
      </div>

      {/* Полные данные */}
      <details style={{
        background: '#f5f5f5',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
      }}>
        <summary style={{cursor: 'pointer', fontWeight: 'bold'}}>
          Полные данные (развернуть для просмотра)
        </summary>
        <div style={{marginTop: '15px'}}>
          <h3>window.bootstrapData (raw)</h3>
          <pre style={{
            background: '#fff',
            padding: '10px',
            borderRadius: '4px',
            overflow: 'auto',
            maxHeight: '300px',
            fontSize: '11px',
          }}>
            {typeof window !== 'undefined' && (window as any).bootstrapData
              ? String((window as any).bootstrapData).substring(0, 2000) + '...'
              : 'Not available'}
          </pre>
          <h3>bootstrapData (full)</h3>
          <pre style={{
            background: '#fff',
            padding: '10px',
            borderRadius: '4px',
            overflow: 'auto',
            maxHeight: '300px',
            fontSize: '11px',
          }}>
            {JSON.stringify(bootstrapData, null, 2).substring(0, 5000) + '...'}
          </pre>
        </div>
      </details>
    </div>
  );
}
